import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertTriangle,
  HelpCircle,
  CheckCircle2,
  FileText,
  Building2,
  ArrowRightLeft,
  ShoppingBag,
  History,
  Loader2,
} from 'lucide-react';
import { ReconciliationIssueRecord, FarmRecord, SupplierRecord } from '@/types/database';
import { reconciliationService, CycleCandidateRecords } from '@/services/reconciliationService';
import { supabase } from '@/lib/supabaseClient';
import { formatCurrency, formatNumber } from '@/lib/utils';

interface CycleResolutionModalProps {
  issue: ReconciliationIssueRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CycleResolutionModal: React.FC<CycleResolutionModalProps> = ({
  issue,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [candidates, setCandidates] = useState<CycleCandidateRecords | null>(null);
  const [farms, setFarms] = useState<FarmRecord[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Decoupled Question 1: Piglet Source / Placement
  const [pigletDecision, setPigletDecision] = useState<
    'internal_transfer' | 'commercial_placement' | 'cycle_transfer' | 'link_sales' | 'historical_incomplete' | 'other'
  >('historical_incomplete');
  const [sourceFarmId, setSourceFarmId] = useState<string>('');
  const [sourceCycleId, setSourceCycleId] = useState<string>('');
  const [placedHeadCount, setPlacedHeadCount] = useState<number>(0);
  const [placementDate, setPlacementDate] = useState<string>('');
  const [supplierId, setSupplierId] = useState<string>('');
  const [basePricePerHead, setBasePricePerHead] = useState<number>(0);
  const [selectedSaleIds, setSelectedSaleIds] = useState<string[]>([]);

  // Decoupled Question 2: Operating Relationship (Never forced/defaulted automatically)
  const [operatingType, setOperatingType] = useState<'contract_grower' | 'company_owned' | ''>('');
  const [adminNotes, setAdminNotes] = useState<string>('');

  useEffect(() => {
    if (!issue || !isOpen) return;

    setError(null);
    setLoading(true);

    const cycleId = issue.cycle_id;
    if (cycleId) {
      reconciliationService.getCycleCandidateRecords(cycleId).then((res) => {
        setCandidates(res);
        if (res.cycle) {
          setOperatingType(res.cycle.operation_type || 'contract_grower');
          setPlacementDate(res.cycle.start_date || new Date().toISOString().split('T')[0]);
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }

    // Load master farms and suppliers
    Promise.all([
      supabase.from('farms').select('*').eq('is_void', false).order('farm_name', { ascending: true }),
      supabase.from('suppliers').select('*').eq('is_void', false).order('supplier_name', { ascending: true }),
    ]).then(([farmsRes, suppsRes]) => {
      setFarms((farmsRes.data as any) || []);
      setSuppliers((suppsRes.data as any) || []);
    });
  }, [issue, isOpen]);

  const handleAcceptHistorical = async () => {
    if (!issue) return;
    setSubmitting(true);
    try {
      await reconciliationService.acceptHistoricalIncomplete(
        issue.id,
        adminNotes || 'Historical incompleteness accepted by Admin without synthetic additions.'
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to accept historical incompleteness');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async () => {
    if (!issue || !issue.cycle_id) return;
    setError(null);
    setSubmitting(true);

    try {
      const selectedFarm = farms.find((f) => f.id === sourceFarmId);

      await reconciliationService.resolveCycleIssue(
        issue.id,
        issue.cycle_id,
        {
          piglet_source_decision: pigletDecision,
          operating_relationship: operatingType ? (operatingType as any) : undefined,
          source_farm_id: sourceFarmId || undefined,
          source_farm_name: selectedFarm?.farm_name,
          source_cycle_id: sourceCycleId || undefined,
          placed_head_count: placedHeadCount > 0 ? Number(placedHeadCount) : undefined,
          placement_date: placementDate || undefined,
          supplier_id: supplierId || undefined,
          base_price_per_head: basePricePerHead > 0 ? Number(basePricePerHead) : undefined,
          linked_sale_ids: selectedSaleIds,
          notes: adminNotes,
        },
        adminNotes
      );

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error resolving cycle issue:', err);
      setError(err.message || 'Failed to resolve cycle issue');
    } finally {
      setSubmitting(false);
    }
  };

  if (!issue) return null;

  const cycle = candidates?.cycle;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
              <HelpCircle className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-slate-900">
                Production Cycle Reconciliation
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Review operational evidence, decoupled piglet placement, and operating relationship.
              </p>
            </div>
          </div>
        </DialogHeader>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Cycle Ground Truth & Operational Facts */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">
              Cycle Metadata & Diagnostic
            </span>
            <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-200 text-slate-700 font-bold">
              {issue.source_sheet}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <span className="text-slate-500">Cycle Code / Batch:</span>
              <div className="font-mono font-bold text-slate-900 mt-0.5">
                {cycle?.cycle_code || issue.legacy_value || 'Unlinked Cycle'}
              </div>
            </div>
            <div>
              <span className="text-slate-500">Grower / Raiser:</span>
              <div className="font-medium text-slate-900 mt-0.5">
                {cycle?.growers?.full_name || cycle?.legacy_raiser_name || 'N/A'}
              </div>
            </div>
            <div>
              <span className="text-slate-500">Farm / Location:</span>
              <div className="text-slate-700 mt-0.5">
                {cycle?.farms?.farm_name || 'Not assigned'}
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-200/80">
            <span className="text-slate-500 font-semibold">Flagged Issue Description:</span>
            <div className="text-slate-800 font-medium mt-0.5">{issue.description}</div>
            {issue.suggested_action && (
              <div className="text-amber-800 bg-amber-50 p-2 rounded border border-amber-200 mt-1.5 text-[11px]">
                💡 {issue.suggested_action}
              </div>
            )}
          </div>
        </div>

        {/* Operational Evidence (Arrivals vs Sales in DB) */}
        {candidates && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div className="p-3 rounded-lg border border-slate-200 bg-white">
              <span className="font-bold text-slate-700 block mb-1">Physical Intake / Arrivals:</span>
              {candidates.existingArrivals.length === 0 ? (
                <div className="text-slate-500 italic text-[11px] bg-slate-50 p-2 rounded">
                  0 recorded arrivals in relational database.
                </div>
              ) : (
                <div className="space-y-1">
                  {candidates.existingArrivals.map((a) => (
                    <div key={a.id} className="flex justify-between text-[11px] font-mono">
                      <span>{a.arrival_date}:</span>
                      <span className="font-bold">{a.head_count} head</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 rounded-lg border border-slate-200 bg-white">
              <span className="font-bold text-slate-700 block mb-1">Recorded Sales:</span>
              {candidates.existingSales.length === 0 ? (
                <div className="text-slate-500 italic text-[11px] bg-slate-50 p-2 rounded">
                  0 sales records linked to this cycle.
                </div>
              ) : (
                <div className="space-y-1">
                  {candidates.existingSales.map((s) => (
                    <div key={s.id} className="flex justify-between text-[11px] font-mono">
                      <span>{s.sale_date}:</span>
                      <span className="font-bold">{s.head_count} head ({formatCurrency(s.total_amount)})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Decoupled Question 1: How were piglets supplied / placed? */}
        <div className="space-y-2.5 pt-1">
          <div className="flex items-center gap-1.5">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-800 font-mono">
              QUESTION 1
            </span>
            <Label className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              How were piglets supplied / placed for this cycle?
            </Label>
          </div>

          <div className="space-y-2">
            {/* Option A: Internal Transfer */}
            <div
              onClick={() => setPigletDecision('internal_transfer')}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                pigletDecision === 'internal_transfer'
                  ? 'border-indigo-600 bg-indigo-50/60 ring-1 ring-indigo-600'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className="flex items-start gap-2.5">
                <input
                  type="radio"
                  name="piglet_decision"
                  checked={pigletDecision === 'internal_transfer'}
                  onChange={() => setPigletDecision('internal_transfer')}
                  className="mt-0.5 text-indigo-600 focus:ring-indigo-500"
                />
                <div className="space-y-1.5 flex-1">
                  <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <ArrowRightLeft className="w-3.5 h-3.5 text-indigo-600" />
                    (A) VSB Internal Transfer (Company-farrowed stock)
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Stock farrowed at VSB sow units (e.g. Cahigam / Sorosoro). Recorded via internal transfer.
                  </p>

                  {pigletDecision === 'internal_transfer' && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 border-t border-indigo-100">
                      <div>
                        <Label className="text-[11px] font-semibold text-slate-700">Source Sow Farm</Label>
                        <select
                          value={sourceFarmId}
                          onChange={(e) => setSourceFarmId(e.target.value)}
                          className="mt-1 w-full text-xs rounded border border-slate-300 p-1.5 bg-white text-slate-900"
                        >
                          <option value="">-- Select Source Farm --</option>
                          {farms.map((f) => (
                            <option key={f.id} value={f.id}>{f.farm_name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-[11px] font-semibold text-slate-700">Transferred Head Count *</Label>
                        <Input
                          type="number"
                          value={placedHeadCount || ''}
                          onChange={(e) => setPlacedHeadCount(parseInt(e.target.value) || 0)}
                          placeholder="e.g. 65"
                          className="mt-1 text-xs h-7"
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] font-semibold text-slate-700">Placement Date</Label>
                        <Input
                          type="date"
                          value={placementDate}
                          onChange={(e) => setPlacementDate(e.target.value)}
                          className="mt-1 text-xs h-7"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Option B: Commercial Placement */}
            <div
              onClick={() => setPigletDecision('commercial_placement')}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                pigletDecision === 'commercial_placement'
                  ? 'border-indigo-600 bg-indigo-50/60 ring-1 ring-indigo-600'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className="flex items-start gap-2.5">
                <input
                  type="radio"
                  name="piglet_decision"
                  checked={pigletDecision === 'commercial_placement'}
                  onChange={() => setPigletDecision('commercial_placement')}
                  className="mt-0.5 text-indigo-600 focus:ring-indigo-500"
                />
                <div className="space-y-1.5 flex-1">
                  <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <ShoppingBag className="w-3.5 h-3.5 text-indigo-600" />
                    (B) Commercial Placement (Missing from Excel Piglets sheet)
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Third-party commercial piglet purchase that lacked separate Ledger 3010 or Piglets sheet logging.
                  </p>

                  {pigletDecision === 'commercial_placement' && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 border-t border-indigo-100">
                      <div>
                        <Label className="text-[11px] font-semibold text-slate-700">Piglet Supplier</Label>
                        <select
                          value={supplierId}
                          onChange={(e) => setSupplierId(e.target.value)}
                          className="mt-1 w-full text-xs rounded border border-slate-300 p-1.5 bg-white text-slate-900"
                        >
                          <option value="">-- Select Supplier --</option>
                          {suppliers.map((s) => (
                            <option key={s.id} value={s.id}>{s.supplier_name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-[11px] font-semibold text-slate-700">Placed Head Count *</Label>
                        <Input
                          type="number"
                          value={placedHeadCount || ''}
                          onChange={(e) => setPlacedHeadCount(parseInt(e.target.value) || 0)}
                          placeholder="e.g. 65"
                          className="mt-1 text-xs h-7"
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] font-semibold text-slate-700">Placement Date</Label>
                        <Input
                          type="date"
                          value={placementDate}
                          onChange={(e) => setPlacementDate(e.target.value)}
                          className="mt-1 text-xs h-7"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Option C: Historical Gap Accepted */}
            <div
              onClick={() => setPigletDecision('historical_incomplete')}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                pigletDecision === 'historical_incomplete'
                  ? 'border-slate-600 bg-slate-100 ring-1 ring-slate-600'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className="flex items-start gap-2.5">
                <input
                  type="radio"
                  name="piglet_decision"
                  checked={pigletDecision === 'historical_incomplete'}
                  onChange={() => setPigletDecision('historical_incomplete')}
                  className="mt-0.5 text-slate-600 focus:ring-slate-500"
                />
                <div>
                  <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <History className="w-3.5 h-3.5 text-slate-600" />
                    (C) Accept Historical Incompleteness (No Placement Record)
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Preserves original evidence with 0 received without inventing or manufacturing placement records.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decoupled Question 2: Operating Relationship */}
        <div className="space-y-2.5 pt-2 border-t border-slate-200">
          <div className="flex items-center gap-1.5">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-800 font-mono">
              QUESTION 2
            </span>
            <Label className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              What is the operating relationship for this cycle?
            </Label>
          </div>
          <p className="text-[11px] text-slate-500">
            Note: Internal piglet placement does NOT force a cycle to Company-Owned. Contract Growers retain their profit split.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div
              onClick={() => setOperatingType('contract_grower')}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                operatingType === 'contract_grower'
                  ? 'border-indigo-600 bg-indigo-50/60 ring-1 ring-indigo-600'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  name="op_type"
                  checked={operatingType === 'contract_grower'}
                  onChange={() => setOperatingType('contract_grower')}
                  className="mt-0.5 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <span className="text-xs font-bold text-slate-900">Contract Grower Operation</span>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Grower provides barn/labor; liquidated via contract profit split / fee.
                  </p>
                </div>
              </div>
            </div>

            <div
              onClick={() => setOperatingType('company_owned')}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                operatingType === 'company_owned'
                  ? 'border-indigo-600 bg-indigo-50/60 ring-1 ring-indigo-600'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  name="op_type"
                  checked={operatingType === 'company_owned'}
                  onChange={() => setOperatingType('company_owned')}
                  className="mt-0.5 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <span className="text-xs font-bold text-slate-900">Company-Owned Operation</span>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    100% internal production; all revenue & costs belong directly to VSB.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resolution Notes */}
        <div>
          <Label className="text-xs font-semibold text-slate-700">Administrator Audit Notes</Label>
          <Input
            type="text"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="e.g. Verified with farm records that piglets were supplied from Cahigam unit"
            className="mt-1 text-xs"
          />
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 justify-between pt-2 border-t border-slate-100">
          <Button
            type="button"
            variant="secondary"
            onClick={handleAcceptHistorical}
            disabled={submitting}
            className="min-h-[44px] text-xs text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 gap-1.5"
          >
            <History className="w-3.5 h-3.5" />
            <span>Accept Historical Incompleteness</span>
          </Button>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              className="min-h-[44px] text-xs font-semibold text-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResolve}
              disabled={submitting}
              className="min-h-[44px] bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 text-xs font-semibold"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Apply & Resolve Issue</span>
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
